import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Loader2 } from "lucide-react";
import { createChart } from "lightweight-charts";
import { api, errMsg, fmt, money } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { PageHeader, SignalBadge, Pnl } from "@/components/Badges";

const PAIRS = ["EUR/USD", "GBP/USD", "USD/JPY", "USD/CHF", "AUD/USD", "NZD/USD", "USD/CAD", "EUR/GBP", "EUR/JPY", "GBP/JPY", "EUR/AUD", "GBP/AUD", "AUD/JPY", "CAD/JPY", "EUR/CAD"];
const d8 = (t) => new Date(t * 1000).toUTCString().slice(5, 22);

function EquityChart({ points }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || points.length < 2) return;
    const chart = createChart(ref.current, {
      height: 280, layout: { background: { color: "#FFFFFF" }, textColor: "#52525B", fontFamily: "JetBrains Mono" },
      grid: { vertLines: { color: "#F4F4F5" }, horzLines: { color: "#F4F4F5" } }, localization: { locale: "en-US" },
      rightPriceScale: { borderColor: "#E4E4E7" }, timeScale: { borderColor: "#E4E4E7", timeVisible: true },
    });
    const up = points[points.length - 1].value >= points[0].value;
    chart.addAreaSeries({ lineColor: up ? "#059669" : "#DC2626", topColor: up ? "#05966933" : "#DC262633", bottomColor: "#ffffff00", lineWidth: 2 }).setData(points);
    chart.timeScale().fitContent();
    const el = ref.current;
    const ro = new ResizeObserver(() => { if (el.isConnected) chart.applyOptions({ width: el.clientWidth }); });
    ro.observe(el);
    return () => { ro.disconnect(); chart.remove(); };
  }, [points]);
  return <div ref={ref} className="w-full h-[280px]" data-testid="equity-chart" />;
}

export default function Backtest() {
  const [f, setF] = useState({ symbol: "EUR/USD", min_confidence: 65, risk_pct: 1, step: 2, max_hold: 48, balance: 10000 });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const history = usePoll("/backtests", 0);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backtest", { ...f, symbol: f.symbol.replace("/", ""), min_confidence: Number(f.min_confidence), risk_pct: Number(f.risk_pct), step: Number(f.step), max_hold: Number(f.max_hold), balance: Number(f.balance) });
      setRes(data);
      history.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const inp = "w-full bg-white border border-zinc-200 rounded-sm px-3 py-2 num text-sm focus:outline-none focus:border-[#007AFF]";
  const field = (k, label, props = {}) => (
    <label className="space-y-1"><span className="label-xs">{label}</span><input className={inp} type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`bt-${k}-input`} {...props} /></label>
  );
  const dg = f.symbol.endsWith("JPY") ? 3 : 5;
  return (
    <div data-testid="backtest-page">
      <PageHeader title="Strategy Backtesting" sub="Replays the live Fx Cotrader engine bar-by-bar on historical candles" />
      <div className="panel p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
          <label className="space-y-1"><span className="label-xs">Pair</span>
            <select className={inp} value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value })} data-testid="bt-symbol-select">{PAIRS.map((p) => <option key={p}>{p}</option>)}</select></label>
          {field("min_confidence", "Min confidence %", { min: 50, max: 95 })}
          {field("risk_pct", "Risk % / trade", { step: 0.25 })}
          {field("balance", "Start balance")}
          {field("max_hold", "Max hold (1H bars)")}
          {field("step", "Eval every N bars", { min: 1, max: 8 })}
          <button onClick={run} disabled={busy} data-testid="bt-run-btn"
            className="flex items-center justify-center gap-2 py-2 bg-[#007AFF] hover:bg-[#1a86ff] text-white text-sm font-semibold rounded-sm transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}{busy ? "Replaying…" : "Run backtest"}
          </button>
        </div>
        <p className="text-[11px] text-zinc-500 mt-3">Only completed candles are visible at each step (no look-ahead). Entries follow the signal's entry type, 50% is banked at TP1 with the stop moved to breakeven, the rest targets TP2. Spread cost is deducted.</p>
      </div>
      {res && (
        <div className="space-y-4 fade-up" data-testid="bt-results">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            {[["Trades", res.trades, "bt-trades"], ["Win rate", res.win_rate != null ? `${res.win_rate}%` : "—", "bt-win-rate"],
              ["Max drawdown", `${res.max_drawdown_pct}%`, "bt-max-dd"], ["Net return", <Pnl v={res.net_return_pct}>{res.net_return_pct}%</Pnl>, "bt-net-return"],
              ["Profit factor", res.profit_factor ?? "—", "bt-profit-factor"], ["Avg R", res.avg_r ?? "—", "bt-avg-r"],
              ["End balance", money(res.end_balance), "bt-end-balance"], ["Data", res.data_source, "bt-data-source"]].map(([l, v, t]) => (
              <div key={l} className="panel p-4" data-testid={t}><p className="label-xs mb-2">{l}</p><div className="num text-lg text-zinc-900">{v}</div></div>
            ))}
          </div>
          <div className="panel p-3">
            <div className="flex justify-between text-[11px] text-zinc-500 mb-2 px-1">
              <span>Equity curve · {res.symbol}</span><span>{res.period_start ? `${d8(res.period_start)} → ${d8(res.period_end)}` : ""} · {res.bars_evaluated} bars evaluated</span>
            </div>
            {res.equity_curve.length > 1 ? <EquityChart points={res.equity_curve} /> : <p className="p-8 text-center text-sm text-zinc-500" data-testid="bt-no-trades">No setups met the confidence threshold in this period.</p>}
          </div>
          <div className="panel overflow-x-auto scrollbar-thin max-h-[420px]">
            <table className="w-full text-sm" data-testid="bt-trades-table">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0"><tr>{["Signal time", "Side", "Conf.", "Regime", "Entry", "SL", "TP1", "TP2", "Outcome", "Bars", "R", "P/L"].map((h) => <th key={h} className="px-3 py-2 text-left label-xs">{h}</th>)}</tr></thead>
              <tbody>
                {res.trade_list.slice().reverse().map((t, i) => (
                  <tr key={i} className="border-b border-zinc-100" data-testid={`bt-trade-${i}`}>
                    <td className="px-3 py-2 num text-[11px] text-zinc-500 whitespace-nowrap">{d8(t.time)}</td><td className="px-3 py-2"><SignalBadge signal={t.direction} /></td>
                    <td className="px-3 py-2 num text-xs text-[#7C3AED]">{t.confidence}%</td><td className="px-3 py-2 text-[11px] text-zinc-500">{t.regime}</td>
                    <td className="px-3 py-2 num text-xs">{fmt(t.entry, dg)}</td><td className="px-3 py-2 num text-xs text-[#DC2626]">{fmt(t.stop_loss, dg)}</td>
                    <td className="px-3 py-2 num text-xs text-[#059669]">{fmt(t.tp1, dg)}</td><td className="px-3 py-2 num text-xs text-[#059669]">{fmt(t.tp2, dg)}</td>
                    <td className="px-3 py-2 text-xs">{t.outcome}</td><td className="px-3 py-2 num text-xs">{t.bars}</td>
                    <td className="px-3 py-2 text-xs"><Pnl v={t.r}>{t.r > 0 ? "+" : ""}{t.r}R</Pnl></td><td className="px-3 py-2 text-xs"><Pnl v={t.pnl}>{money(t.pnl)}</Pnl></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {res.trades < 20 && <p className="text-xs text-[#D97706]" data-testid="bt-sample-warning">Small sample: only {res.trades} trades in this window. Win rate and drawdown are not statistically reliable yet.</p>}
          <p className="text-[11px] text-zinc-500">{res.notes} Past performance does not guarantee future results.</p>
        </div>
      )}
      <div className="panel mt-4" data-testid="bt-history">
        <h3 className="px-4 py-3 border-b border-zinc-200 font-display font-bold text-sm">Recent runs</h3>
        <table className="w-full text-sm"><tbody>
          {(history.data || []).map((h, i) => (
            <tr key={i} className="border-b border-zinc-100" data-testid={`bt-history-${i}`}>
              <td className="px-4 py-2 num font-bold">{h.symbol}</td><td className="px-3 py-2 text-xs text-zinc-500">≥{h.min_confidence}% · {h.risk_pct}% risk</td>
              <td className="px-3 py-2 num text-xs">{h.trades} trades</td><td className="px-3 py-2 num text-xs">{h.win_rate ?? "—"}% win</td>
              <td className="px-3 py-2 num text-xs">DD {h.max_drawdown_pct}%</td><td className="px-3 py-2 text-xs"><Pnl v={h.net_return_pct}>{h.net_return_pct}%</Pnl></td>
              <td className="px-3 py-2 text-[11px] text-zinc-500">{h.data_source}</td><td className="px-4 py-2 num text-[11px] text-zinc-400">{new Date(h.created_at).toUTCString().slice(5, 22)}</td>
            </tr>
          ))}
          {!history.data?.length && <tr><td className="p-4 text-xs text-zinc-500">No backtests yet.</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}
